import hotkey from '@atlas/tool/box/event/hotkey';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import useMemoState from '@atlas/tool/box/hooks/useMemoState';
import useScreenBreakpoint, { ScreenBreakpoint } from '@atlas/tool/box/hooks/useScreenBreakpoint';
import isNotFalsy from '@atlas/tool/box/isNotFalsy';
import OptionsList, {
    SOptionsList,
    type TOptionsListChooseHandler,
    type TOptionsListItem,
    type TOptionsListItemTooltip,
    type TOptionsListProps,
    type TOptionsListRef,
} from '@atlas/uicorn/lib/Control/OptionsList';
import Text from '@atlas/uicorn/lib/Display/Text';
import MagnifyingGlassIcon from '@atlas/uicorn/lib/Icon/MagnifyingGlassIcon';
import PlusIcon from '@atlas/uicorn/lib/Icon/PlusIcon';
import Spinner from '@atlas/uicorn/lib/Display/Spinner';
import type { TExample } from '@atlas/uicorn/preview/modules';
import { debounce } from 'lodash-es';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import useRefCallback from 'use-ref-callback';
import useRequest from 'use-request';
import ChevronIcon from '@atlas/uicorn/lib/Icon/ChevronIcon';
import type { TPopoverPlacement, TPopoverRef } from '@atlas/uicorn/lib/Display/Popover';
import Popover from '@atlas/uicorn/lib/Display/Popover';
import useOnChange from '@atlas/tool/box/hooks/useOnChange';
import useLastRef from '@atlas/tool/box/hooks/useLastRef';
import window from '@atlas/tool/box/browser/window';
import Option from '@atlas/uicorn/lib/Control/Option';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';
import RefreshIcon from '@atlas/uicorn/lib/Icon/RefreshIcon';
import searchByScore from '@atlas/tool/box/searchByScore';

function MenuComponent<TOption extends TMenuItem = TMenuItem>(
    props: TMenuProps<TOption>,
    ref: React.ForwardedRef<TMenuRef>,
): React.ReactElement {
    const {
        onChoose,
        onSelect,
        onCreate,
        onlyInlineCreate, // Show create option only when search is not empty
        createOptionLabel,
        defaultSelected,
        selected: propsSelected,
        items,
        title,
        disabled,
        autoFocus,
        autoSelect,
        search,
        noItemsMessage = defaultNoItemsMessage,
        debounceSearch,
        sort,
        keepCursor,
        searchSuffix,
        showSearchRefresh,
        placeholder = 'Search...',
        groupSelected,
        inlineDescription,
        createButtonPosition = 'start',
        selectAll,
        nestedMenuPlacement = 'right-start',
        heightLimit,
        ...elementProps
    } = props;

    const menuRef = useRef<HTMLDivElement>(null);

    const isMobileScreen = useScreenBreakpoint(ScreenBreakpoint.MAX_MOBILE);

    const [selected, setSelected] = useControlledState<TOption[]>(propsSelected, undefined, defaultSelected ?? []);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const [query, setQuery] = useMemoState<string>(
        (previous = '') => {
            if (typeof search === 'string') return search;
            if (!search) return '';
            return previous;
        },
        [search],
    );
    const hasQuery = !!query.length;

    const [extraItems, setExtraItems] = useMemoState<TOption[]>(() => [], [query]);
    const lastQueryRef = useLastRef(query);

    const itemsRequest = useRequest<TOption[], unknown, unknown[]>((refresh: boolean) => {
        if (typeof search === 'function') {
            return search(query.trim(), refresh, (extra: TOption[]) => {
                if (lastQueryRef.current.trim() !== query.trim()) return;
                setExtraItems((existing) => existing.concat(extra));
            });
        }

        if (items) {
            if (!search || !query) return items;
            const skipSort = sort === false || typeof sort === 'function';
            return defaultItemsFilter<TOption>(items, query, skipSort);
        }

        return [];
    }, []);

    const itemsRequestValue = useMemo<TOption[]>(() => {
        return (itemsRequest.value ?? [])?.concat(extraItems);
    }, [itemsRequest.value, extraItems]);

    const debouncedItemsRequest = useMemo(() => {
        return debounce(itemsRequest.execute, debounceSearch === true ? 300 : debounceSearch || 0);
    }, [itemsRequest.execute, debounceSearch]);

    const handleRefresh = useRefCallback(() => debouncedItemsRequest(true));

    useOnChange(
        function callDebouncedSearch() {
            debouncedItemsRequest();
        },
        [query, search],
    );

    useOnChange(
        function ifNoSearchIsUsedItemsShouldUpdateImmediately() {
            itemsRequest.execute();
        },
        [items],
    );

    const handleSelect = useRefCallback(
        (keys: TOption['key'][], keyInAction: TOption['key'], addedToSelection: boolean) => {
            const lookupItemByKey = (key: TOption['key']) =>
                itemsRequestValue?.find((item) => item.key === key) ??
                (selected.find((item) => item.key === key) as TOption);

            const selectedItems = keys.map(lookupItemByKey).filter(isNotFalsy);
            onSelect?.(selectedItems, lookupItemByKey(keyInAction), addedToSelection);
            setSelected(selectedItems);
        },
    );

    const handleClear = useRefCallback(() => {
        handleSelect([], '', false);
    });

    const handleSelectAll = useRefCallback(
        selectAll && onSelect
            ? () => {
                  if (!itemsRequestValue) return;

                  const allItems = itemsRequestValue.filter((item) => !item.selectable);

                  handleSelect(
                      allItems.map((item) => item.key),
                      '',
                      true,
                  );
              }
            : undefined,
    );

    const handleFilterQueryInputChange = useRefCallback<React.ChangeEventHandler<HTMLInputElement>>((event) =>
        setQuery(event.target.value),
    );

    const hasSearch = search && typeof search !== 'string';

    const [optionsList, setOptionsList] = useState<TOptionsListRef | null>(null);
    const optionsListRef = useLastRef(optionsList);

    useEffect(
        function autoFocusOnOptionIfSearchHidden() {
            if (!autoFocus) return;
            if (hasSearch) return;
            if (!menuRef.current) return;
            if ('focus' in menuRef.current) {
                menuRef.current?.focus();
            }
        },
        [optionsList],
    );

    useImperativeHandle<TMenuRef | null, TMenuRef | null>(
        ref,
        () =>
            optionsList
                ? {
                      ...optionsList,
                      focus() {
                          menuRef.current?.focus();
                      },
                  }
                : null,
        [optionsList],
    );

    const invisibleSearchRef = useRef({ time: 0, value: '' });

    const handleKeyDown = useRefCallback<React.KeyboardEventHandler<HTMLDivElement>>((event) => {
        if (event.defaultPrevented) return;

        if (!hasSearch && optionsList) {
            if (event.key.match(/^[a-z]$/)) {
                event.preventDefault();
                event.stopPropagation();
                const now = Date.now();
                if (now - invisibleSearchRef.current.time > 1e3) {
                    invisibleSearchRef.current.time = now;
                    invisibleSearchRef.current.value = event.key;
                } else {
                    invisibleSearchRef.current.value += event.key;
                }
                const matchingItem = itemsWithActions.find((item) =>
                    item.label.toLowerCase().startsWith(invisibleSearchRef.current.value),
                );
                if (matchingItem) optionsList.setCursor(matchingItem.key);
            } else {
                invisibleSearchRef.current.time = 0;
                invisibleSearchRef.current.value = '';
            }
        }

        if (escapeHotkey.matches(event)) {
            // When Search input is focused, it does useful default things on Escape key
            if (event.target !== searchInputRef.current && hasQuery) {
                event.preventDefault();
                event.stopPropagation();
            }
            optionsList?.removeCursor();
            searchInputRef.current?.focus();
            return;
        }

        if (downHotkey.matches(event)) {
            event.preventDefault();
            event.stopPropagation();
            optionsList?.moveCursorToNext();
            return;
        }

        if (upHotkey.matches(event)) {
            event.preventDefault();
            event.stopPropagation();
            optionsList?.moveCursorToPrevious();
            return;
        }

        if (enterHotkey.matches(event)) {
            event.preventDefault();
            event.stopPropagation();
            optionsList?.chooseCursor();
            return;
        }

        if (
            downAndSpaceHotkey.matches(event) ||
            upAndSpaceHotkey.matches(event) ||
            spaceAndSpaceHotkey.matches(event) ||
            enterAndSpaceHotkey.matches(event)
        ) {
            event.preventDefault();
            event.stopPropagation();
            if (onSelect) optionsList?.selectCursor();
            else optionsList?.chooseCursor();
            return;
        }

        if (!hasSearch && spaceHotkey.matches(event)) {
            event.preventDefault();
            event.stopPropagation();
            if (onSelect) optionsList?.selectCursor();
            else optionsList?.chooseCursor();
            return;
        }
    });

    const handleChoose = useRefCallback<TOptionsListChooseHandler<TOption>>((eventOption, selection) => {
        const originalOption = itemsRequestValue?.find((item) => item.key === eventOption.key);
        const option = (!originalOption?.lazy && originalOption) || eventOption;
        if (onCreate && option.key === createActionKey) {
            onCreate(query);
            return;
        }

        if (!('options' in option) || !option.options?.length) {
            onChoose?.(option, selection);
            // Ignore options with sub menu
            return;
        }
    });

    // False if there is no create option and string otherwise
    const createQuery: string | false = onCreate ? query || (onlyInlineCreate ? false : '') : false;

    const missingSelectedOptions = useMemo<TOption[] | null>(() => {
        if (!groupSelected) return null;
        if (hasQuery) return null;
        const present = itemsRequestValue;
        const missing = present?.length
            ? selected.filter((option) => present.every((item) => item.key !== option.key))
            : selected;
        if (!missing.length) return null;
        return missing.map((o) => ({ ...o, group: groupSelected === true ? 'Selected' : groupSelected }));
    }, [groupSelected, hasQuery, itemsRequestValue, selected]);

    const optionsListItems = useMemo<TOption[]>(() => {
        let items = itemsRequestValue ?? [];

        if (missingSelectedOptions) items.push(...missingSelectedOptions);

        items = items.map((item) => {
            if (!('options' in item) || !item.options?.length) return item as TOption;

            const { options, ...data } = item;

            let popover: TPopoverRef | null = null;
            let subMenu: TMenuRef | null = null;

            const handleNestedChoose: typeof handleChoose = (option) => {
                handleChoose(option, false);
                popover?.close();
            };

            const nestedMenu = (
                <SNestedMenuPopover
                    ref={(p) => {
                        popover = p;
                    }}
                    placement={nestedMenuPlacement}
                    onKeyDown={(e) => {
                        if (escapeHotkey.matches(e) || leftHotkey.matches(e)) {
                            popover?.close();
                            menuRef.current?.focus();
                        }
                    }}
                    content={
                        <SNestedMenu
                            ref={(m) => {
                                subMenu = m;
                            }}
                            items={options as TOption[]}
                            onChoose={handleNestedChoose}
                        />
                    }
                >
                    <ChevronIcon dir="right" />
                </SNestedMenuPopover>
            );

            let openTimer: NodeJS.Timeout | null = null;
            let closeTimer: NodeJS.Timeout | null = null;

            const handleMouseEnter = (
                e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                data: Omit<TOption, 'options'>,
                popover: TPopoverRef | null,
            ) => {
                data.onMouseEnter?.(e);
                if (e.defaultPrevented) return;

                if (closeTimer) {
                    clearTimeout(closeTimer);
                    closeTimer = null;
                }

                openTimer = setTimeout(() => {
                    popover?.open();
                }, NESTED_MENU_SWITCH_TIMEOUT);
            };

            const handleMouseLeave = (
                e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                data: Omit<TOption, 'options'>,
                popover: TPopoverRef | null,
            ) => {
                data.onMouseLeave?.(e);
                if (e.defaultPrevented) return;

                if (openTimer) {
                    clearTimeout(openTimer);
                    openTimer = null;
                }

                closeTimer = setTimeout(() => {
                    popover?.close();
                }, NESTED_MENU_SWITCH_TIMEOUT);
            };

            return {
                ...data,
                onMouseEnter: (e) => handleMouseEnter(e, data, popover),
                onMouseLeave: (e) => handleMouseLeave(e, data, popover),
                ...({
                    onChoose: () => {
                        if (!popover) return;
                        if (!popover.isOpen()) popover.open();
                        setTimeout(() => {
                            subMenu?.focus();
                            subMenu?.setCursor();
                        });
                    },
                } as any),
                suffix: data.suffix ? (
                    <>
                        {data.suffix}
                        {nestedMenu}
                    </>
                ) : (
                    nestedMenu
                ),
            } as TOption;
        });

        if (!items.length) return items;

        items = sort ? items.sort(typeof sort === 'function' ? sort : defaultItemsSort) : items;

        return items;
    }, [itemsRequestValue, missingSelectedOptions, sort, handleChoose]);

    const createAction = useMemo<TOption>(
        () =>
            ({
                key: createActionKey,
                label:
                    (typeof createOptionLabel === 'function'
                        ? createOptionLabel(createQuery || '')
                        : createOptionLabel) || `Create ${createQuery || 'new'}...`,
                selectable: false,
                prefix: <PlusIcon size={18} />,
            } as TOption),
        [createQuery, createOptionLabel],
    );

    const itemsWithActions = useMemo<TOption[]>(() => {
        const listItemsWithActions = [...optionsListItems];

        if (typeof createQuery === 'string') {
            if (createButtonPosition === 'start') listItemsWithActions.unshift(createAction);
            else listItemsWithActions.push(createAction);
        }

        if (props.customActions?.length) {
            listItemsWithActions.unshift(
                ...props.customActions.map((item) => ({
                    ...item,
                    selectable: false,
                })),
            );
        }

        return listItemsWithActions.map((item) => ({
            ...item,
            onMouseEnter: (event: React.MouseEvent<HTMLButtonElement>): void => {
                if (item.onMouseEnter) {
                    item.onMouseEnter(event);
                    if (event.defaultPrevented) return;
                }

                if (!optionsListRef.current) return;
                if (!menuRef.current) return;
                if (!window) return;
                if (!window.document.activeElement) return;
                if (!menuRef.current.contains(window.document.activeElement)) return;

                optionsListRef.current.setCursor(item.key);
            },
        }));
    }, [createAction, createQuery, optionsListItems, createButtonPosition, props.customActions]);

    const selectedKeys = useMemo(() => selected.map((item) => item.key), [selected]);

    useEffect(
        function autoSelectFirstOption() {
            if (!autoSelect) return;
            if (itemsWithActions.length === 0) return;
            optionsList?.setCursor(itemsWithActions[0].key);
        },
        [autoSelect, itemsWithActions, optionsList, selectedKeys],
    );

    const allItemsSelected = !!itemsRequestValue?.length && selectedKeys.length === itemsRequestValue.length;
    const noItemsSelected = selectedKeys.length === 0;

    return (
        <SMenu tabIndex={0} {...elementProps} onKeyDown={handleKeyDown} ref={menuRef} $heightLimit={heightLimit}>
            {(title || hasSearch) && (
                <SMenuHeader $disabled={disabled} $empty={!itemsWithActions.length && !noItemsMessage}>
                    {typeof title === 'string' ? (
                        <SMenuTitle caption>
                            <span>{title}</span>
                        </SMenuTitle>
                    ) : (
                        title
                    )}
                    {hasSearch && (
                        <SMenuSearch>
                            <SMenuSearchInput
                                autoFocus={autoFocus && !isMobileScreen}
                                data-autofocus={autoFocus}
                                type="search"
                                placeholder={placeholder}
                                value={query}
                                onChange={handleFilterQueryInputChange}
                                disabled={disabled}
                                ref={searchInputRef}
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck="false"
                            />
                            {itemsRequest.pending ? (
                                <SMenuSearchSpinner />
                            ) : showSearchRefresh ? (
                                <IconButton
                                    icon={<RefreshIcon color="primary" />}
                                    tooltip="Refresh"
                                    onClick={handleRefresh}
                                />
                            ) : searchSuffix === undefined ? (
                                <SMenuSearchIcon />
                            ) : typeof searchSuffix === 'function' ? (
                                searchSuffix()
                            ) : (
                                searchSuffix
                            )}
                        </SMenuSearch>
                    )}
                </SMenuHeader>
            )}
            {handleSelectAll && handleClear && (
                <SMenuSelectControls>
                    <SSelectControl onClick={handleSelectAll} disabled={allItemsSelected}>
                        Select all
                    </SSelectControl>
                    <SSelectControl onClick={handleClear} disabled={noItemsSelected}>
                        Clear
                    </SSelectControl>
                </SMenuSelectControls>
            )}

            {itemsWithActions.length ? (
                <OptionsList<TOption>
                    items={itemsWithActions}
                    disabled={disabled}
                    onChoose={handleChoose}
                    onSelect={onSelect && handleSelect}
                    selected={selectedKeys}
                    keepCursor={keepCursor}
                    inlineDescription={inlineDescription}
                    ref={setOptionsList}
                    onMouseEnter={(e) => {
                        e.preventDefault();
                    }}
                />
            ) : null}

            {!optionsListItems.length && noItemsMessage ? (
                <SOptionsListEmptyMessage>
                    {typeof noItemsMessage === 'function'
                        ? noItemsMessage({ loading: itemsRequest.pending, query })
                        : noItemsMessage}
                </SOptionsListEmptyMessage>
            ) : null}
        </SMenu>
    );
}

const Menu = React.memo(React.forwardRef(MenuComponent)) as <TOption extends TMenuItem = TMenuItem>(
    props: TMenuProps<TOption> & { ref?: React.Ref<TMenuRef> },
) => React.ReactElement<TMenuProps<TOption>>;

export default Menu;

// #region Helpers

const createActionKey = '__atlas-menu-create-action';

const downHotkey = hotkey('down', 'hidden');
const leftHotkey = hotkey('left', 'hidden');
const upHotkey = hotkey('up', 'hidden');
const enterHotkey = hotkey('enter', 'hidden');
const spaceHotkey = hotkey('space', 'hidden');
const upAndSpaceHotkey = hotkey(['up', 'space'], 'hidden');
const downAndSpaceHotkey = hotkey(['down', 'space'], 'hidden');
const spaceAndSpaceHotkey = hotkey(['space', 'space'], 'hidden');
const enterAndSpaceHotkey = hotkey(['enter', 'space'], 'hidden');
const escapeHotkey = hotkey('escape', 'hidden');

const NESTED_MENU_SWITCH_TIMEOUT = 350;

export function defaultItemsFilter<TOption extends TMenuItem = TMenuItem>(
    items: TOption[],
    query: string,
    skipSort?: boolean,
) {
    if (!query) return items;

    const filterQueries = query.toLowerCase().split(/\s+/g).filter(isNotFalsy);
    if (!filterQueries.length) return items;

    return searchByScore(items, query, ['label', 'group', 'description'], skipSort);
}

export function defaultItemsSort(a: TMenuItem, b: TMenuItem) {
    if (a.group && !b.group) return 1;
    if (!a.group && b.group) return -1;
    if (a.group && b.group && a.group !== b.group) return a.group.localeCompare(b.group);
    return a.label.localeCompare(b.label);
}

function defaultNoItemsMessage({ loading }: { loading: boolean }) {
    return loading ? 'Loading...' : 'We were unable to find any results 😓';
}

// #endregion

// #region Styles

export const SOptionsListEmptyMessage = styled.div`
    font: ${(p) => p.theme.font.normal};
    margin: 0;
    padding: calc(var(--options-list-item-spacing) / 2) var(--options-list-item-spacing);
    color: ${(p) => p.theme.color.grey8};
    text-align: center;
`;

export const SMenuTitle = styled(Text)``;

const searchSuffixAppearanceKeyframes = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const SMenuSearchIcon = styled(MagnifyingGlassIcon)`
    animation: ${searchSuffixAppearanceKeyframes} 0.2s ease;
`;

const SMenuSearchSpinner = styled(Spinner)`
    animation: ${searchSuffixAppearanceKeyframes} 0.2s ease;
`;

const SSelectControl = styled(Option)`
    color: ${(p) => p.theme.color.primary};
`;

export const SMenuSearchInput = styled.input`
    font: ${(p) => p.theme.font.input};
    color: ${(p) => p.theme.color.grey9};
    border: unset;
    background: unset;
    border-radius: unset;
    padding: 0;

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: ${(p) => p.theme.color.grey8};
    }
`;

export const SMenuSearch = styled.div`
    // the number may change with updated inputs. previously, the icon was setting the height but it may be disabled
    min-height: 2.9rem;
    color: ${(p) => p.theme.color.grey8};
    display: flex;
    align-items: center;
    gap: var(--options-menu-spacing);

    ${SMenuSearchInput} {
        flex: 1;
    }
`;

const SMenuSelectControls = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: calc(0.4rem / 2) 0.4rem;
    font: ${(p) => p.theme.font.normal};
`;

export const SMenuHeader = styled.header<{ $disabled?: boolean; $empty?: boolean }>`
    display: flex;
    flex-direction: column;
    transition: opacity 0.2s ease;
    opacity: ${(p) => (p.$disabled ? 0.2 : 1)};

    & > :where(*) {
        padding: 0 var(--options-menu-spacing) var(--options-menu-spacing);

        &:last-child {
            border-bottom: 0.1rem solid ${(p) => p.theme.color.grey6};
        }

        ${(p) =>
            p.$empty
                ? css`
                      &:last-child {
                          border-bottom-color: transparent;
                      }
                  `
                : ''}
    }
`;

export const SMenu = styled.div<{ $heightLimit?: string }>`
    --options-menu-spacing: 0.4rem;

    display: inline-flex;
    flex-direction: column;
    gap: var(--options-menu-spacing);

    &:focus-visible {
        outline: none;
    }

    ${SOptionsList} {
        flex: 1;
        overflow: auto;
        max-height: ${(p) => p.$heightLimit ?? 'none'};
    }
`;

export const SMenuOptionDescription = styled(Text).attrs({ p: true })`
    font: ${(p) => p.theme.font.caption};
    color: ${(p) => p.theme.color.grey8};
`;

const SNestedMenu = styled(Menu)`
    min-width: 10rem;
    width: 100%;
` as typeof Menu;

export const SNestedMenuPopover = styled(Popover)`
    max-width: 30rem;
    max-height: 32rem;
    display: flex;
    z-index: 1070; // +10 to ContextMenu popovers
` as typeof Popover;

// #endregion

// #region Types

export type TMenuRef = TOptionsListRef & { focus: () => void };

export type TMenuItemTooltip = TOptionsListItemTooltip;

export type TMenuItem = TOptionsListItem & { options?: TOptionsListItem[]; keywords?: string[] };

export type TMenuSearch<TOption extends TMenuItem = TMenuItem> = (
    query: string,
    refresh?: boolean,
    results?: (extra: TOption[]) => void,
) => TOption[] | Promise<TOption[]>;
export type TMenuSort<TOption extends TMenuItem = TMenuItem> = (a: TOption, b: TOption) => number;

export type TMenuElementProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'title' | 'onSelect'>;

export type TMenuProps<TOption extends TMenuItem = TMenuItem> = TMenuElementProps &
    Pick<TOptionsListProps<TOption>, 'disabled' | 'keepCursor' | 'inlineDescription'> & {
        title?: React.ReactNode;
        autoFocus?: boolean;
        autoSelect?: boolean;
        items?: TOption[] | null;
        search?: null | boolean | string | TMenuSearch<TOption>;
        debounceSearch?: boolean | number;
        placeholder?: string;
        defaultSelected?: TOption[];
        selected?: TOption[];
        sort?: null | boolean | TMenuSort<TOption>;
        onChoose?: TOptionsListChooseHandler<TOption>;
        onSelect?: (selectedOptions: TOption[], optionInAction: TOption, addedToSelection: boolean) => void;
        searchSuffix?: React.ReactNode | (() => React.ReactNode);
        showSearchRefresh?: boolean;
        groupSelected?: boolean | string;
        onCreate?: (query?: string) => void;
        onlyInlineCreate?: boolean;
        createOptionLabel?: string | ((query: string) => string);
        noItemsMessage?: null | string | ((props: { loading: boolean; query: string }) => React.ReactNode);
        createButtonPosition?: 'start' | 'end';
        selectAll?: boolean;
        customActions?: TOption[];
        nestedMenuPlacement?: TPopoverPlacement;
        heightLimit?: string;
    };

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        const style: React.CSSProperties = {
            ...(props.limitHeight && { height: 200 }),
            ...(props.background && {
                background: props.background,
                boxShadow: '0 0 0.1rem 0rem #999',
                padding: '0.4rem',
                borderRadius: '0.4rem',
            }),
        };

        const ref = useRef<TMenuRef>(null);

        props.moveCursorToPrevious?.(() => ref.current?.moveCursorToPrevious());
        props.moveCursorToNext?.(() => ref.current?.moveCursorToNext());
        props.setCursor?.(() => ref.current?.setCursor());
        props.setCursorOnZachary?.(() => ref.current?.setCursor('zachary'));
        props.chooseCursor?.(() => ref.current?.chooseCursor());
        props.removeCursor?.(() => ref.current?.removeCursor());

        const items = useMemo<TMenuItem[]>(
            () => [
                {
                    key: 'jeannie',
                    label: 'Jeannie Balistreri',
                    ...(props.icons && { icon: '👨🏼‍🚀' }),
                    ...(props.descriptions && { description: 'Who knows where will this life bring us' }),
                    ...(props.hotkeys && { hotkey: hotkey('w', 'hidden') }),
                    renderLabel: ({ label }) => <em>{label}</em>,
                },
                {
                    key: 'kasey',
                    label: 'Kasey Lebovski',
                    ...(props.icons && { icon: '👨🏼‍🎤' }),
                    ...(props.descriptions && { description: 'Musician' }),
                    ...(props.hotkeys && { hotkey: hotkey('alt+m', 'hidden') }),
                    ...(props.groups && { group: 'Artists' }),
                },
                {
                    key: 'daniel',
                    label: 'Daniel Conor',
                    ...(props.icons && { icon: '👩🏼‍🎓' }),
                    ...(props.descriptions && { description: 'Student' }),
                    ...(props.hotkeys && { hotkey: hotkey('ctrl+shift+s', 'hidden') }),
                    ...(props.groups && { group: 'Education' }),
                },
                {
                    key: 'zachary',
                    label: 'Zachary Heller',
                    ...(props.icons && { icon: '👨🏼‍🏫' }),
                    ...(props.descriptions && { description: 'Teacher' }),
                    ...(props.hotkeys && { hotkey: hotkey('shift+t', 'hidden') }),
                    ...(props.groups && { group: 'Education' }),
                },
                {
                    key: 'adam',
                    label: 'Adam Lebovski',
                    ...(props.icons && { icon: '👨🏼‍💻' }),
                    ...(props.descriptions && { description: 'Software Engineer' }),
                    ...(props.hotkeys && { hotkey: hotkey('ctrl+s', 'hidden') }),
                    ...(props.groups && { group: 'Tech' }),
                },
                {
                    key: 'verna',
                    label: 'Verna Hane PhD',
                    ...(props.icons && { icon: '👨🏼‍🔬' }),
                    ...(props.descriptions && { description: 'Scientist' }),
                    ...(props.hotkeys && { hotkey: hotkey('ctrl+shift+alt+s', 'hidden') }),
                    ...(props.groups && { group: 'Tech' }),
                },
                ...(props.longName
                    ? [
                          {
                              key: 'long-name',
                              label: 'Majesty Queen Isabella Seraphina Anastasia Augustina Bianca Leonardo Georgiana Evangeline Ophelia Victoria',
                              ...(props.icons && { icon: '👸' }),
                              ...(props.descriptions && { description: 'Queen' }),
                              ...(props.hotkeys && { hotkey: hotkey('ctrl+q', 'hidden') }),
                              ...(props.groups && { group: 'City' }),
                          },
                      ]
                    : []),
                {
                    key: 'violet',
                    label: 'Miss Violet Hegmann',
                    ...(props.icons && { icon: '👩🏼‍🏭' }),
                    ...(props.descriptions && { description: 'Policewoman' }),
                    ...(props.hotkeys && { hotkey: hotkey('ctrl+p', 'hidden') }),
                    ...(props.groups && { group: 'City' }),
                },
                {
                    key: 'jo',
                    label: 'Jo Powlowski',
                    ...(props.icons && { icon: '👷🏼‍♀️' }),
                    ...(props.descriptions && { description: 'Construction worker' }),
                    ...(props.hotkeys && { hotkey: hotkey('ctrl+alt+c', 'hidden') }),
                    ...(props.groups && { group: 'City' }),
                },
                {
                    key: 'james',
                    label: 'James Ronalds',
                    ...(props.icons && { icon: '👵🏼' }),
                    ...(props.descriptions && { description: 'Retired' }),
                    ...(props.hotkeys && { hotkey: hotkey('ctrl+r', 'hidden') }),
                    ...(props.groups && { group: 'City' }),
                },
                {
                    key: 'ronald',
                    label: 'Ronald James',
                    ...(props.icons && { icon: '👨🏼‍🍳' }),
                    ...(props.descriptions && { description: 'Chief' }),
                    ...(props.hotkeys && { hotkey: hotkey('ctrl+c', 'hidden') }),
                    ...(props.groups && { group: 'Food' }),
                },
                {
                    key: 'susan',
                    label: 'Susan Adams',
                    ...(props.icons && { icon: '👩🏼‍🌾' }),
                    ...(props.descriptions && { description: 'Farmer' }),
                    ...(props.hotkeys && { hotkey: hotkey('ctrl+f', 'hidden') }),
                    ...(props.groups && { group: 'Food' }),
                },
            ],
            [props.icons, props.descriptions, props.hotkeys, props.groups, props.longName],
        );

        const search = useMemo<boolean | TMenuSearch>(() => {
            if (props.search === 'Default' || typeof props.search === 'undefined') return true;

            if (props.search === 'Custom: Case sensitive') {
                return (query: string) => {
                    props.searchCallback(query);
                    return items.filter((item) =>
                        typeof item.label === 'string' ? item.label.includes(query) : false,
                    );
                };
            }

            if (props.search === 'Async data') {
                return (query: string) => {
                    props.searchCallback(query);
                    return new Promise((resolve) =>
                        setTimeout(
                            resolve,
                            1e3,
                            items
                                .filter((item) =>
                                    typeof item.label === 'string'
                                        ? item.label.toLowerCase().includes(query.toLowerCase())
                                        : false,
                                )
                                .slice(0, 5),
                        ),
                    );
                };
            }

            return false;
        }, [props.search, items]);

        // Props not initialized yet
        if (!props.search) return null;

        const dataProps = typeof search === 'function' ? { search } : { search, items };

        return (
            <Menu
                ref={ref}
                style={style}
                title={props.title}
                disabled={props.disabled}
                onChoose={props.onChoose}
                onSelect={props.selectable ? props.onSelect : undefined}
                onCreate={props.withCreate ? props.onCreate : undefined}
                sort={props.sort}
                groupSelected={props.groupSelected}
                debounceSearch={props.debounceSearch}
                {...dataProps}
            />
        );
    },
    controls: {
        moveCursorToPrevious: 'callback',
        moveCursorToNext: 'callback',
        setCursor: 'callback',
        setCursorOnZachary: 'callback',
        chooseCursor: 'callback',
        removeCursor: 'callback',
        title: { type: 'string', initial: 'Guests' },
        selectable: { type: 'boolean', initial: true },
        disabled: 'boolean',
        limitHeight: { type: 'boolean', initial: false, required: true },
        search: ['Default', 'Custom: Case sensitive', 'Async data', 'No'],
        debounceSearch: 'boolean',
        sort: 'boolean',
        background: { type: 'color:var', initial: 'grey0' },
        icons: 'boolean',
        descriptions: 'boolean',
        hotkeys: 'boolean',
        groups: 'boolean',
        longName: 'boolean',
        groupSelected: {
            type: 'string',
            connected: false,
            initial: 'Selected',
            hint: 'Enable Async Search to see effect',
        },
        withCreate: { type: 'boolean', initial: false },
    },
    handlers: {
        onChoose: 'console',
        onSelect: 'console',
        onCreate: 'console',
        searchCallback: 'console',
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=28-6083&mode=design&t=TVklSB9u5fm4OQmw-0',
    ],
};

export const NestedExample: TExample = {
    render(props) {
        const items = useMemo<TMenuItem[]>(
            () => [
                {
                    key: 'open',
                    label: 'Open',
                },
                {
                    key: 'open-with',
                    label: 'Open with...',
                    options: [
                        { key: 'browser', label: 'Browser' },
                        { key: 'editor', label: 'Text editor' },
                    ],
                },
                {
                    key: 'save-as',
                    label: 'Save as...',
                    options: [
                        { key: 'txt', label: 'Text' },
                        { key: 'pdf', label: 'PDF' },
                    ],
                },
            ],
            [],
        );

        return (
            <Menu
                style={{
                    background: 'var(--color-grey0)',
                    boxShadow: '0 0 0.1rem 0rem #999',
                    padding: '0.4rem',
                    borderRadius: '0.4rem',
                }}
                title={props.title}
                disabled={props.disabled}
                onChoose={props.onChoose}
                items={items}
            />
        );
    },
    controls: {},
    handlers: {
        onChoose: 'console',
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=28-6083&mode=design&t=TVklSB9u5fm4OQmw-0',
    ],
};

export const LazyExample: TExample = {
    render(props) {
        const items = useMemo<TMenuItem[]>(
            () => [
                {
                    key: 'first-name',
                    label: 'Enter first name',
                    lazy: (option) => ({ ...option, value: prompt('Enter first name') }),
                },
                {
                    key: 'last-name',
                    label: 'Enter last name',
                    lazy: (option) => ({ ...option, value: prompt('Enter last name') }),
                },
            ],
            [],
        );

        return (
            <Menu
                style={{
                    background: 'var(--color-grey0)',
                    boxShadow: '0 0 0.1rem 0rem #999',
                    padding: '0.4rem',
                    borderRadius: '0.4rem',
                }}
                title={props.title}
                disabled={props.disabled}
                onChoose={props.onChoose}
                items={items}
            />
        );
    },
    controls: {},
    handlers: {
        onChoose: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Menu', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
